import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { User } from "../types/user.types";
import { Center } from "../types/center.types";
import { Patient } from "../types/patient.types";
import { Variant, VariantOption } from "../types/earmold.types";
import _ from "lodash";
import { Command, CommandStatus, CommandType } from "../types/command.types";
import { createCommand, updateCommand } from "../mutations/command.mutations";
import { SavedFile } from "../types/files.types";

export enum NewOrderStep {
	CHOOSE_AUDIO_RESPONSIBLE = 0,
	CHOOSE_PATIENT = 1,
	CHOOSE_COMMAND_TYPE = 2,
	CHOOSE_EARPRINT_SHIPPING_TYPE = 3,
	CHOOSE_SETUP = 4,
	CHOOSE_SHIPPING = 5,
	CHOOSE_RENEW_REASON = 6,
	CHOOSE_RENEW_COMMANDS = 7,
	CHOOSE_RENEW_SETUP = 8,
	VALIDATION = 1000,
}

export enum NewOrderType {
	NEW = "NEW",
	REWORK = "REWORK",
}

export enum NewOrderEarSide {
	LEFT = "LEFT",
	RIGHT = "RIGHT",
}

export enum NewOrderEarprintShippingType {
	NATUS_OTOSCAN = "NATUS_OTOSCAN",
	DIGITAL = "DIGITAL",
	PHYSICAL = "PHYSICAL",
	REWORK_OLD_COMMAND = "REWORK_OLD_COMMAND",
}

export enum NewOrderRenewReason {
	RENEW = "RENEW",
	CHANGE_EARMOLD_TYPE = "CHANGE_EARMOLD_TYPE",
	CHANGE_CARACTERISTICS = "CHANGE_CARACTERISTICS",
	BROKEN = "BROKEN",
	LOST = "LOST",
	OTHER = "OTHER",
}

export type NewOrderForm = { [key: string]: VariantOption | undefined | null };

export interface OrderStoreState {
	/**
	 * STEP
	 */
	step: NewOrderStep;
	/**
	 * CENTER
	 */
	center?: Center;
	/**
	 * COMMAND
	 */
	command?: Command;
	/**
	 * AUDIO RESPONSIBLE
	 */
	audioResponsible?: User;
	/**
	 * CREATOR
	 */
	creator?: User;
	/**
	 * PATIENT
	 */
	patient?: Patient;
	/**
	 * EXTERNAL NUMBER ID
	 */
	externalNumberId?: string;
	/**
	 * COMMAND TYPE
	 */
	type?: NewOrderType;
	/**
	 * EARPRINT SHIPPING TYPE
	 */
	earprintShippingType?: NewOrderEarprintShippingType;
	/**
	 * DATE
	 */
	date?: Date;
	/**
	 * REWORK COMMAND
	 */
	reworkCommand?: Command;
	reworkCommandOld?: string;
	/**
	 * SETUP
	 */
	hasRightEar?: boolean;
	hasLeftEar?: boolean;
	earmold?: string;
	variants?: Variant[];
	rightEarForm: NewOrderForm;
	leftEarForm: NewOrderForm;
	commentary?: string;
	leftFile?: SavedFile;
	rightFile?: SavedFile;
	additionalFiles?: SavedFile[];
	renewReason?: NewOrderRenewReason;
	updating?: boolean;
}

interface OrderStoreActions {
	/**
	 * STEP
	 */
	setStep: (step: NewOrderStep) => void;
	getStep: () => NewOrderStep;
	goPreviousStep: () => void;
	canGoNextStep: () => boolean;
	goNextStep: () => void;
	/**
	 * CENTER
	 */
	setCenter: (center?: Center) => void;
	getCenter: () => Center | undefined;
	/**
	 * AUDIO RESPONSIBLE
	 */
	setAudioResponsible: (audio?: User) => void;
	getAudioResponsible: () => User | undefined;
	/**
	 * CREATOR
	 */
	setCreator: (user?: User) => void;
	/**
	 * PATIENT
	 */
	setPatient: (patient?: Patient) => void;
	/**
	 * EXTERNAL NUMBER ID
	 */
	setExternalNumberId: (externalNumberId?: string) => void;
	/**
	 * COMMAND TYPE
	 */
	setType: (type?: NewOrderType) => void;
	/**
	 * REWORK COMMAND
	 */
	setReworkCommand: (command?: string | Command) => void;
	/**
	 * EARPRINT SHIPPING TYPE
	 */
	setEarprintShippingType: (type?: NewOrderEarprintShippingType) => void;
	setFile: (side: NewOrderEarSide, file: SavedFile) => void;
	/**
	 * DATE
	 */
	setDate: (date: Date) => void;
	/**
	 * SETUP
	 */
	enableEar: (side: NewOrderEarSide, state?: boolean) => void;
	setEarmold: (earmold: string, variants?: Variant[]) => void;
	getVariantValue: (
		side: NewOrderEarSide,
		label: string,
	) => VariantOption | undefined | null;
	getForm: (size: NewOrderEarSide) => NewOrderForm | undefined;
	setVariantValue: (
		side: NewOrderEarSide,
		label: string,
		value: VariantOption,
	) => void;
	setCommentary: (commentary: string) => void;
	setAdditionalFiles: (files?: SavedFile[]) => void;
	isDisabled: (
		variant: Variant,
		option: VariantOption,
		form?: NewOrderForm,
	) => boolean;
	setRenewReason: (reason?: NewOrderRenewReason) => void;
	isRework: () => boolean;
	setIsUpdate: (updating?: boolean) => void;
	isUpdate: () => boolean;
	/**
	 * STORE ACTIONS
	 */
	reset: () => void;
	save: () => OrderStoreState;
	load: (state: OrderStoreState) => void;
	reload: () => void;
	saveDraft: (options?: {
		status?: CommandStatus;
		log?: boolean;
	}) => Promise<void>;
	updateCommand: () => Promise<void>;
	fromCommand: (command: Command) => void;
	reworkFromCommand: (command: Command) => void;
	copyMostFulfilled: () => void;
	closeWithoutSaving: () => void;
}

const initialState: OrderStoreState = {
	step: NewOrderStep.CHOOSE_AUDIO_RESPONSIBLE,
	center: undefined,
	command: undefined,
	audioResponsible: undefined,
	creator: undefined,
	patient: undefined,
	externalNumberId: undefined,
	type: undefined,
	earprintShippingType: undefined,
	hasRightEar: undefined,
	hasLeftEar: undefined,
	earmold: undefined,
	rightEarForm: {},
	leftEarForm: {},
	commentary: undefined,
	date: undefined,
	leftFile: undefined,
	rightFile: undefined,
	additionalFiles: undefined,
	renewReason: undefined,
	reworkCommand: undefined,
	reworkCommandOld: undefined,
	updating: false,
};

export const useNewOrderStore = create<OrderStoreState & OrderStoreActions>()(
	devtools(
		persist(
			(set, get) => ({
				...initialState,
				setStep: (step: NewOrderStep) => {
					set(() => ({ step }));
				},
				getStep: () => get().step,
				goPreviousStep: () => {
					switch (get().step) {
						case NewOrderStep.CHOOSE_PATIENT: {
							set(() => ({ step: NewOrderStep.CHOOSE_AUDIO_RESPONSIBLE }));
							break;
						}
						case NewOrderStep.CHOOSE_COMMAND_TYPE: {
							set(() => ({ step: NewOrderStep.CHOOSE_PATIENT }));
							break;
						}
						case NewOrderStep.CHOOSE_EARPRINT_SHIPPING_TYPE: {
							if (get().type === NewOrderType.NEW) {
								set(() => ({
									step: NewOrderStep.CHOOSE_COMMAND_TYPE,
								}));
							}
							if (get().type === NewOrderType.REWORK) {
								set(() => ({
									step: NewOrderStep.CHOOSE_COMMAND_TYPE,
								}));
							}
							break;
						}
						case NewOrderStep.CHOOSE_SETUP: {
							set(() => ({ step: NewOrderStep.CHOOSE_EARPRINT_SHIPPING_TYPE }));
							break;
						}
						case NewOrderStep.CHOOSE_SHIPPING: {
							set(() => ({ step: NewOrderStep.CHOOSE_SETUP }));
							break;
						}
						case NewOrderStep.VALIDATION: {
							set(() => ({ step: NewOrderStep.CHOOSE_SHIPPING }));
							break;
						}
						case NewOrderStep.CHOOSE_RENEW_REASON: {
							set(() => ({ step: NewOrderStep.CHOOSE_COMMAND_TYPE }));
							break;
						}
						case NewOrderStep.CHOOSE_RENEW_COMMANDS: {
							set(() => ({ step: NewOrderStep.CHOOSE_RENEW_REASON }));
							break;
						}
						case NewOrderStep.CHOOSE_RENEW_SETUP: {
							set(() => ({ step: NewOrderStep.CHOOSE_RENEW_COMMANDS }));
							break;
						}
					}
				},
				canGoNextStep: () => {
					switch (get().step) {
						case NewOrderStep.CHOOSE_AUDIO_RESPONSIBLE: {
							return get().audioResponsible !== undefined;
						}
						case NewOrderStep.CHOOSE_PATIENT: {
							return get().patient !== undefined;
						}
						case NewOrderStep.CHOOSE_COMMAND_TYPE: {
							return get().type !== undefined;
						}
						case NewOrderStep.CHOOSE_EARPRINT_SHIPPING_TYPE: {
							return get().earprintShippingType !== undefined;
						}
						case NewOrderStep.CHOOSE_SETUP: {
							if (get().earmold === undefined) {
								return false;
							}
							if (get().hasLeftEar) {
								if (
									get().earprintShippingType ===
										NewOrderEarprintShippingType.DIGITAL &&
									get().leftFile === undefined
								) {
									return false;
								}
								const leftForm = get().leftEarForm;
								if (
									Object.values(leftForm).every((v) => v !== undefined) ===
									false
								) {
									return false;
								}
							}
							if (get().hasRightEar) {
								if (
									get().earprintShippingType ===
										NewOrderEarprintShippingType.DIGITAL &&
									get().rightFile === undefined
								) {
									return false;
								}
								const rightForm = get().rightEarForm;
								if (
									Object.values(rightForm).every((v) => v !== undefined) ===
									false
								) {
									return false;
								}
							}
							return get().hasLeftEar === true || get().hasRightEar === true;
						}
						case NewOrderStep.CHOOSE_SHIPPING: {
							return get().date !== undefined;
						}
						case NewOrderStep.VALIDATION: {
							return true;
						}
						case NewOrderStep.CHOOSE_RENEW_REASON: {
							return get().renewReason !== undefined;
						}
						case NewOrderStep.CHOOSE_RENEW_COMMANDS: {
							return (
								get().reworkCommand !== undefined ||
								(get().reworkCommandOld !== undefined &&
									get().reworkCommandOld?.trim() !== "")
							);
						}
						case NewOrderStep.CHOOSE_RENEW_SETUP: {
							if (get().earmold === undefined) {
								return false;
							}
							if (get().hasLeftEar) {
								if (
									get().earprintShippingType ===
										NewOrderEarprintShippingType.DIGITAL &&
									get().leftFile === undefined
								) {
									return false;
								}
								const leftForm = get().leftEarForm;
								if (
									Object.values(leftForm).every((v) => v !== undefined) ===
									false
								) {
									return false;
								}
							}
							if (get().hasRightEar) {
								if (
									get().earprintShippingType ===
										NewOrderEarprintShippingType.DIGITAL &&
									get().rightFile === undefined
								) {
									return false;
								}
								const rightForm = get().rightEarForm;
								if (
									Object.values(rightForm).every((v) => v !== undefined) ===
									false
								) {
									return false;
								}
							}
							return get().hasLeftEar === true || get().hasRightEar === true;
						}
						default:
							return false;
					}
				},
				goNextStep: () => {
					switch (get().step) {
						case NewOrderStep.CHOOSE_AUDIO_RESPONSIBLE: {
							set(() => ({ step: NewOrderStep.CHOOSE_PATIENT }));
							break;
						}
						case NewOrderStep.CHOOSE_PATIENT: {
							set(() => ({ step: NewOrderStep.CHOOSE_COMMAND_TYPE }));
							break;
						}
						case NewOrderStep.CHOOSE_COMMAND_TYPE: {
							if (get().type === NewOrderType.NEW) {
								set(() => ({
									step: NewOrderStep.CHOOSE_EARPRINT_SHIPPING_TYPE,
								}));
							}
							if (get().type === NewOrderType.REWORK) {
								set(() => ({
									step: NewOrderStep.CHOOSE_RENEW_REASON,
								}));
							}
							break;
						}
						case NewOrderStep.CHOOSE_EARPRINT_SHIPPING_TYPE: {
							set(() => ({ step: NewOrderStep.CHOOSE_SETUP }));
							break;
						}
						case NewOrderStep.CHOOSE_SETUP: {
							set(() => ({ step: NewOrderStep.CHOOSE_SHIPPING }));
							break;
						}
						case NewOrderStep.CHOOSE_SHIPPING: {
							set(() => ({ step: NewOrderStep.VALIDATION }));
							break;
						}
						case NewOrderStep.CHOOSE_RENEW_REASON: {
							set(() => ({ step: NewOrderStep.CHOOSE_RENEW_COMMANDS }));
							break;
						}
						case NewOrderStep.CHOOSE_RENEW_COMMANDS: {
							set(() => ({ step: NewOrderStep.CHOOSE_RENEW_SETUP }));
							break;
						}
						case NewOrderStep.CHOOSE_RENEW_SETUP: {
							set(() => ({ step: NewOrderStep.CHOOSE_SHIPPING }));
							break;
						}
					}
				},
				setCenter: (center?: Center) => {
					set(() => ({
						center,
						patient: undefined,
						audioResponsible: undefined,
					}));
				},
				getCenter: () => get().center,
				setAudioResponsible: (audio?: User) => {
					set(() => ({ audioResponsible: audio }));
				},
				setCreator: (user?: User) => {
					set(() => ({ creator: user }));
				},
				getAudioResponsible: () => get().audioResponsible,
				setPatient: (patient?: Patient) => {
					set(() => ({ patient }));
				},
				setExternalNumberId: (externalNumberId?: string) =>
					set(() => ({ externalNumberId })),
				setType: (type?: NewOrderType) => {
					if (type !== NewOrderType.REWORK) {
						set(() => ({
							reworkCommand: undefined,
							reworkCommandOld: undefined,
						}));
					}
					set(() => ({ type }));
				},
				setEarprintShippingType: (type?: NewOrderEarprintShippingType) =>
					set(() => ({ earprintShippingType: type })),
				setFile: (side: NewOrderEarSide, file: SavedFile) => {
					if (side === NewOrderEarSide.LEFT) {
						set(() => ({ leftFile: file }));
					}
					if (side === NewOrderEarSide.RIGHT) {
						set(() => ({ rightFile: file }));
					}
				},
				enableEar: (side: NewOrderEarSide, state?: boolean) => {
					if (side === NewOrderEarSide.LEFT) {
						set(() => ({ hasLeftEar: state }));
					}
					if (side === NewOrderEarSide.RIGHT) {
						set(() => ({ hasRightEar: state }));
					}
				},
				setEarmold: (earmold: string, variants?: Variant[]) => {
					/** Set variant and reset right, and left forms */
					set(() => ({
						earmold,
						variants,
						rightEarForm: variants
							? variants
									.map((v) => v.id)
									.reduce((p, v) => ({ ...p, [v]: undefined }), {})
							: {},
						leftEarForm: variants
							? variants
									.map((v) => v.id)
									.reduce((p, v) => ({ ...p, [v]: undefined }), {})
							: {},
					}));
				},
				getVariantValue: (side: NewOrderEarSide, label: string) => {
					switch (side) {
						case NewOrderEarSide.LEFT:
							return get().leftEarForm[label];
						case NewOrderEarSide.RIGHT:
							return get().rightEarForm[label];
						default:
							return undefined;
					}
				},
				getForm: (side: NewOrderEarSide) => {
					switch (side) {
						case NewOrderEarSide.LEFT:
							return get().leftEarForm;
						case NewOrderEarSide.RIGHT:
							return get().rightEarForm;
						default:
							return undefined;
					}
				},
				setVariantValue: (
					side: NewOrderEarSide,
					label: string,
					value: VariantOption,
				) => {
					if (typeof label != "string") {
						return;
					}
					switch (side) {
						case NewOrderEarSide.LEFT: {
							// 1. Get current state
							const copyLeftEarForm = get().leftEarForm;
							// 2. Set variant value
							copyLeftEarForm[label] = value;
							// 3. Invalidate all later choices
							const invalidatedCopyLeftEarForm = Object.fromEntries(
								Object.entries(copyLeftEarForm).map((value) => {
									const key = value[0];
									const val = value[1];
									const id = Number.parseInt(key);
									if (id > Number.parseInt(label)) {
										const variants = get().variants;
										if (Array.isArray(variants)) {
											const variant = variants.find(
												(variant) => variant.id === id,
											);
											if (variant !== undefined) {
												const optionsDisabled = variant?.options.data.map(
													(option) =>
														get().isDisabled(
															variant,
															option,
															get().leftEarForm,
														),
												);
												// In this case, no options can be selected by the user.
												if (_.every(optionsDisabled)) {
													return [key, null];
												}
												return [key, undefined];
											} else {
												return [key, undefined];
											}
										} else {
											return [key, undefined];
										}
									}
									return [key, val];
								}),
							);
							// 4. Set store
							return set(() => ({
								leftEarForm: invalidatedCopyLeftEarForm,
							}));
						}
						case NewOrderEarSide.RIGHT: {
							// 1. Get current state
							const copyRightEarForm = get().rightEarForm;
							// 2. Set variant value
							copyRightEarForm[label] = value;
							// 3. Invalidate all later choices
							const invalidatedCopyRightEarForm = Object.fromEntries(
								Object.entries(copyRightEarForm).map((value) => {
									const key = value[0];
									const val = value[1];
									const id = Number.parseInt(key);
									if (id > Number.parseInt(label)) {
										const variants = get().variants;
										if (Array.isArray(variants)) {
											const variant = variants.find(
												(variant) => variant.id === id,
											);
											if (variant !== undefined) {
												const optionsDisabled = variant?.options.data.map(
													(option) =>
														get().isDisabled(
															variant,
															option,
															get().rightEarForm,
														),
												);
												// In this case, no options can be selected by the user.
												if (_.every(optionsDisabled)) {
													return [key, null];
												}
												return [key, undefined];
											} else {
												return [key, undefined];
											}
										} else {
											return [key, undefined];
										}
									}
									return [key, val];
								}),
							);
							// 4. Set store
							return set(() => ({
								rightEarForm: invalidatedCopyRightEarForm,
							}));
						}
						default:
							return undefined;
					}
				},
				setCommentary: (commentary: string) => set(() => ({ commentary })),
				isDisabled: (
					variant: Variant,
					option: VariantOption,
					form?: NewOrderForm,
				) => {
					if (form === undefined) {
						return false;
					}
					let flagAffectedByAtLeastOneCondition = false;
					for (const condition of variant.options.conditions) {
						// 1. Validate if option is affected by condition
						if (option && !condition.targets.includes(option.uuid)) {
							continue;
						}
						flagAffectedByAtLeastOneCondition = true;
						// 2.0 Apply condition
						// 2.1 AND
						const shouldAllBeTrue = [];
						for (const subcondition of condition.conditions) {
							// If condition as no variant linked to it
							if (subcondition.variantId === null) {
								continue;
							}
							// If user has not select an option yet for this feature
							if (!(subcondition.variantId in form)) {
								continue;
							}
							const subconditionOption = form[subcondition.variantId];
							switch (subcondition.operator) {
								case "==": {
									shouldAllBeTrue.push(
										subcondition.values.findIndex(
											(value) =>
												subconditionOption && value === subconditionOption.uuid,
										) !== -1,
									);
									break;
								}
								case "!=": {
									shouldAllBeTrue.push(
										subcondition.values.findIndex(
											(value) =>
												subconditionOption && value === subconditionOption.uuid,
										) === -1,
									);
									break;
								}
								default:
									throw Error("unknown operator");
							}
						}
						if (_.every(shouldAllBeTrue)) {
							return false;
						}
					}
					// 0. Not affected by any condition, will always be displayed
					return flagAffectedByAtLeastOneCondition;
				},
				reset: () => set(initialState),
				saveDraft: async (options?: {
					status?: CommandStatus;
					log?: boolean;
				}) => {
					const command = get().command;
					let type = undefined;
					switch (get().earprintShippingType) {
						case NewOrderEarprintShippingType.DIGITAL:
							type = CommandType.THREED;
							break;
						case NewOrderEarprintShippingType.NATUS_OTOSCAN:
							type = CommandType.OTOSCAN;
							break;
						case NewOrderEarprintShippingType.PHYSICAL:
							type = CommandType.POSTE;
							break;
						default:
							break;
					}
					const draftCopy = { ...get() };
					if (get().isRework()) {
						type = CommandType.REWORK;
					}
					if (!draftCopy.hasLeftEar) {
						draftCopy.leftEarForm = {};
						draftCopy.hasLeftEar = false;
					}
					if (!draftCopy.hasRightEar) {
						draftCopy.rightEarForm = {};
						draftCopy.hasRightEar = false;
					}
					if (command === undefined) {
						// Create command
						// 0. Extract variables
						const center = get().center;
						const patient = get().patient;
						// 1. Assert on variables
						if (center === undefined) {
							throw "Center is mandatory";
						}
						const command = await createCommand({
							centerId: center.id,
							patientId: patient?.id ?? undefined,
							status: options?.status ?? CommandStatus.DRAFT,
							type: type,
							draft: draftCopy,
							shippingAddressId: center.shippingAddressId,
							billingAddressId: center.enterprise?.addressId,
							externalId: get().externalNumberId,
							creatorId: get().creator?.id,
							managerId: get().audioResponsible?.id,
						});
						set(() => ({ command }));
					} else {
						// 0. Extract variables
						const center = get().center;
						const patient = get().patient;
						if (center === undefined) {
							throw "Center is mandatory";
						}
						const updatedCommand = await updateCommand({
							id: command.id,
							centerId: center.id,
							patientId: patient?.id ?? undefined,
							status: options?.status,
							type: type,
							draft: { ...draftCopy, command: undefined },
							shippingAddressId: center.shippingAddressId,
							billingAddressId: center.enterprise?.addressId,
							externalId: get().externalNumberId,
							creatorId: get().creator?.id,
							managerId: get().audioResponsible?.id,
							log: options?.log,
						});
						set(() => ({ command: updatedCommand }));
					}
				},
				updateCommand: async () => {
					const command = get().command;
					let type = undefined;
					switch (get().earprintShippingType) {
						case NewOrderEarprintShippingType.DIGITAL:
							type = CommandType.THREED;
							break;
						case NewOrderEarprintShippingType.NATUS_OTOSCAN:
							type = CommandType.OTOSCAN;
							break;
						case NewOrderEarprintShippingType.PHYSICAL:
							type = CommandType.POSTE;
							break;
						default:
							break;
					}
					if (get().isRework()) {
						type = CommandType.REWORK;
					}
					if (command !== undefined) {
						// 0. Extract variables
						const center = get().center;
						const patient = get().patient;
						if (center === undefined) {
							throw "Center is mandatory";
						}
						const updatedCommand = await updateCommand({
							id: command.id,
							centerId: center.id,
							patientId: patient?.id ?? undefined,
							type: type,
							draft: get(),
							shippingAddressId: center.shippingAddressId,
							billingAddressId: center.enterprise?.addressId,
							creatorId: get().creator?.id,
							managerId: get().audioResponsible?.id,
						});
						set(() => ({ command: updatedCommand }));
					}
				},
				setDate: (date: Date) => {
					set(() => ({ date }));
				},
				setAdditionalFiles: (files?: SavedFile[]) => {
					set(() => ({ additionalFiles: files }));
				},
				save: () => {
					return get();
				},
				load: (state: OrderStoreState) => {
					// TODO be sure this is not breaking
					if (state.command?.status === CommandStatus.DRAFT && state.variants) {
						if (state.hasRightEar) {
							const rightEarForm: NewOrderForm = {};
							for (const variant of state.variants) {
								rightEarForm[variant.id] = state.rightEarForm[variant.id];
							}
							state.rightEarForm = rightEarForm;
						}
						if (state.hasLeftEar) {
							const leftEarForm: NewOrderForm = {};
							for (const variant of state.variants) {
								leftEarForm[variant.id] = state.leftEarForm[variant.id];
							}
							state.leftEarForm = leftEarForm;
						}
					}
					get().reset();
					set(() => state);
				},
				reload: () => {
					const state = get();
					get().load(state);
				},
				setReworkCommand: (command?: string | Command) => {
					if (command === undefined) {
						set(() => ({
							reworkCommand: undefined,
							reworkCommandOld: undefined,
						}));
					} else if (typeof command === "string") {
						set(() => ({
							reworkCommand: undefined,
							reworkCommandOld: command,
							earprintShippingType:
								NewOrderEarprintShippingType.REWORK_OLD_COMMAND,
							earmold: undefined,
							leftEarForm: {},
							rightEarForm: {},
						}));
					} else {
						set(() => ({
							reworkCommand: command,
							reworkCommandOld: undefined,
							hasLeftEar: command.draft?.hasLeftEar,
							hasRightEar: command.draft?.hasRightEar,
							leftEarForm: command.draft?.leftEarForm,
							rightEarForm: command.draft?.rightEarForm,
						}));
					}
				},
				setRenewReason: (reason?: NewOrderRenewReason) => {
					set(() => ({ renewReason: reason }));
				},
				fromCommand: (command: Command) => {
					set(() => ({
						earmold: command.draft?.earmold,
						leftEarForm: command.draft?.leftEarForm,
						rightEarForm: command.draft?.rightEarForm,
						leftFile: command.draft?.leftFile,
						rightFile: command.draft?.rightFile,
					}));
				},
				reworkFromCommand: (command: Command) => {
					const draft = command.draft;
					set(() => ({
						...draft,
						type: NewOrderType.REWORK,
						reworkCommand: command,
						command: undefined,
						date: undefined,
						step: NewOrderStep.CHOOSE_RENEW_REASON,
					}));
				},
				isRework: () => {
					return (
						get().reworkCommand !== undefined ||
						get().reworkCommandOld !== undefined
					);
				},
				setIsUpdate: (updating?: boolean) => {
					set(() => ({ updating }));
				},
				isUpdate: () => {
					return get().updating === true;
				},
				copyMostFulfilled: () => {
					const hasLeft = get().hasLeftEar;
					const hasRight = get().hasRightEar;
					if (hasLeft && !hasRight) {
						// 0. Enable right and copy left to right
						set(() => ({
							hasRightEar: true,
							rightEarForm: { ...get().leftEarForm },
						}));
					} else if (!hasLeft && hasRight) {
						// 0. Enable right and copy left to right
						set(() => ({
							hasLeftEar: true,
							leftEarForm: { ...get().rightEarForm },
						}));
					} else {
						const countLeft = Object.values(get().leftEarForm).reduce(
							(p, v) => (v === undefined ? p : p + 1),
							0,
						);
						const countRight = Object.values(get().rightEarForm).reduce(
							(p, v) => (v === undefined ? p : p + 1),
							0,
						);
						if (countLeft <= countRight) {
							set(() => ({
								leftEarForm: { ...get().rightEarForm },
							}));
						} else {
							set(() => ({
								rightEarForm: { ...get().leftEarForm },
							}));
						}
					}
				},
				closeWithoutSaving: async () => {
					get().reset();
				},
			}),
			{
				name: "order-store",
				version: 1,
				getStorage: () => localStorage,
			},
		),
	),
);
